// Platform Admin → Dashboard. Severity-ordered: Issues first (only the
// problems, hidden when there are none), then Live / Revenue / Users /
// AI / Totals. One reusable stat tile, color only on issues — keeps the
// surface minimal.

import { type AdminAnalytics, useAdminAnalytics } from "@/hooks/useAdmin";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { useDict, fmt } from "@/i18n/LocaleProvider";

// ─── formatters ────────────────────────────────────────────────

function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function fmtMoneyCents(cents: number, currency: string): string {
  const v = cents / 100;
  if (currency === "BRL") {
    return `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (currency === "USD") {
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${v.toFixed(2)} ${currency}`;
}

// ─── primitives ────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] text-muted uppercase tracking-[0.1em] mt-6 mb-2 pb-2 border-b border-stroke">
      {children}
    </div>
  );
}

function Tile({
  label, value, sub,
}: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="px-4 py-3">
      <div className="font-display text-[24px] font-semibold text-content leading-none">
        {value}
      </div>
      <div className="font-mono text-[10px] text-muted uppercase tracking-[0.05em] mt-2">
        {label}
      </div>
      {sub && (
        <div className="font-mono text-[10px] text-dim mt-[2px]">{sub}</div>
      )}
    </div>
  );
}

function IssueRow({
  label, value, severity, hint,
}: { label: string; value: string | number; severity: "error" | "warning"; hint?: string }) {
  const color = severity === "error" ? "rgb(var(--color-danger))" : "rgb(var(--color-warning))";
  return (
    <div
      className="flex items-baseline gap-3 px-3 py-2 mb-1 rounded"
      style={{
        background: `${color}08`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      <span
        className="font-display text-[16px] font-bold tabular-nums"
        style={{ color }}
      >
        {value}
      </span>
      <span className="font-mono text-[11px] text-content">{label}</span>
      {hint && (
        <span className="font-mono text-[10px] text-dim ml-auto">{hint}</span>
      )}
    </div>
  );
}

// 100% stacked thin bar for distributions (plan, role) — no chart lib.
function DistroBar({ items }: { items: Array<{ label: string; count: number; color: string }> }) {
  const total = items.reduce((a, b) => a + b.count, 0) || 1;
  return (
    <div className="px-4 py-3">
      <div className="flex h-2 w-full rounded overflow-hidden">
        {items.map((it) => (
          <div
            key={it.label}
            title={`${it.label}: ${it.count}`}
            style={{
              width: `${(it.count / total) * 100}%`,
              background: it.color,
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {items.map((it) => (
          <div key={it.label} className="flex items-center gap-1 font-mono text-[10px] text-muted">
            <span className="w-2 h-2 rounded-sm" style={{ background: it.color }} />
            <span className="text-content">{it.count}</span>
            <span>{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── sections ──────────────────────────────────────────────────

function IssuesSection({ d }: { d: AdminAnalytics }) {
  const t = useDict().app.adminDashboard;
  const rows: Array<React.ReactNode> = [];
  const h = d.health;

  if (h.unresolved_alerts > 0) {
    rows.push(
      <IssueRow
        key="alerts"
        severity="error"
        value={h.unresolved_alerts}
        label={t.unresolvedAlerts}
        hint={t.triageHint}
      />,
    );
  }
  if (h.failed_scans_24h > 0) {
    rows.push(
      <IssueRow
        key="scans"
        severity={h.failed_scans_24h > 5 ? "error" : "warning"}
        value={h.failed_scans_24h}
        label={t.scansFailed}
      />,
    );
  }
  if (h.failed_logins_24h >= 20) {
    rows.push(
      <IssueRow
        key="logins"
        severity={h.failed_logins_24h >= 100 ? "error" : "warning"}
        value={h.failed_logins_24h}
        label={t.failedLogins}
        hint={t.bruteForce}
      />,
    );
  }
  const totalQueue = (h.queue.scans ?? 0) + (h.queue.enrich ?? 0);
  if (totalQueue >= 20) {
    rows.push(
      <IssueRow
        key="queue"
        severity={totalQueue >= 100 ? "error" : "warning"}
        value={totalQueue}
        label={fmt(t.jobsBacklogged, { scans: h.queue.scans ?? 0, enrich: h.queue.enrich ?? 0 })}
      />,
    );
  }
  const disk = h.storage.disk;
  if (disk && disk.percent_used >= 80) {
    rows.push(
      <IssueRow
        key="disk"
        severity={disk.percent_used >= 90 ? "error" : "warning"}
        value={`${disk.percent_used}%`}
        label={fmt(t.diskUsedOn, { path: disk.path })}
      />,
    );
  }

  return (
    <>
      <SectionLabel>{t.issuesHealth}</SectionLabel>
      {rows.length === 0 ? (
        <div className="font-mono text-[12px] text-success px-3 py-2">
          {t.allHealthy}
        </div>
      ) : (
        <div>{rows}</div>
      )}
      {/* storage line — informational, always shown but quiet */}
      <div className="font-mono text-[10px] text-dim px-3 py-2 flex flex-wrap gap-x-4">
        <span>DB {fmtBytes(h.storage.db_bytes)}</span>
        <span>Redis {fmtBytes(h.storage.redis_bytes)}</span>
        {disk && <span>Disk {disk.percent_used}% ({fmtBytes(disk.used_bytes)} / {fmtBytes(disk.total_bytes)})</span>}
        <span>{t.queueLabel}: scans {h.queue.scans ?? "—"} · enrich {h.queue.enrich ?? "—"}</span>
      </div>
    </>
  );
}

// ─── colors for distributions ──────────────────────────────────
const PLAN_COLORS: Record<string, string> = {
  free: "#6b7280",
  basic: "#3b82f6",
  pro: "rgb(var(--color-accent))",
  enterprise: "#a855f7",
};
const ROLE_COLORS: Record<string, string> = {
  admin: "rgb(var(--color-accent))",
  pentester: "#3b82f6",
  viewer: "#6b7280",
  client: "#a855f7",
};

// ─── component ─────────────────────────────────────────────────

export function AdminDashboardTab() {
  const t = useDict().app.adminDashboard;
  const q = useAdminAnalytics();

  if (q.isLoading) return <Loading text={t.loading} />;
  if (q.isError || !q.data) {
    return (
      <Card className="border-danger">
        <span className="text-danger font-mono text-[13px]">
          {t.loadError}
        </span>
      </Card>
    );
  }
  const d = q.data;

  const planItems = Object.entries(d.revenue.plan_distribution).map(
    ([slug, count]) => ({
      label: slug,
      count,
      color: PLAN_COLORS[slug] ?? "#9ca3af",
    }),
  );
  const roleItems = Object.entries(d.users.role_distribution).map(
    ([role, count]) => ({
      label: role,
      count,
      color: ROLE_COLORS[role.toLowerCase()] ?? "#9ca3af",
    }),
  );
  const mrrEntries = Object.entries(d.revenue.mrr_cents);

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div className="font-mono text-[11px] text-dim">
          {t.asOf} {new Date(d.as_of).toLocaleString()}
        </div>
        <button
          onClick={() => q.refetch()}
          className="font-mono text-[10px] text-muted hover:text-content uppercase tracking-[0.05em] bg-transparent border-none cursor-pointer"
        >
          {t.refresh}
        </button>
      </div>

      {/* 1. Issues first */}
      <IssuesSection d={d} />

      {/* 2. Live */}
      <SectionLabel>{t.sectionLive}</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2">
        <Tile label={t.scansToday} value={d.live.scans_today} />
        <Tile label={t.scansInFlight} value={d.live.scans_in_flight} />
        <Tile label={t.usersOnline15} value={d.live.users_online} />
      </div>

      {/* 3. Revenue & growth */}
      <SectionLabel>{t.sectionRevenue}</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2">
        {mrrEntries.length === 0 ? (
          <Tile label={t.mrr} value="—" />
        ) : (
          mrrEntries.map(([cur, cents]) => (
            <Tile
              key={cur}
              label={`${t.mrr} · ${cur}`}
              value={fmtMoneyCents(cents, cur)}
            />
          ))
        )}
        <Tile label={t.newSubsWk} value={d.revenue.new_subs_week} />
        <Tile label={t.canceledWk} value={d.revenue.cancels_week} />
        <Tile label={t.trials7d} value={d.revenue.trials_expiring_7d} />
      </div>
      {planItems.length > 0 && (
        <div className="mt-2">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.05em] px-4">
            {t.planDistribution}
          </div>
          <DistroBar items={planItems} />
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 mt-2">
        <Tile label={t.newOrgsWk} value={d.revenue.new_orgs_week} />
        <Tile label={t.newOrgsMo} value={d.revenue.new_orgs_month} />
        <Tile label={t.newUsersWk} value={d.revenue.new_users_week} />
        <Tile label={t.freeToPaid} value={`${d.revenue.free_to_paid_pct}%`} />
      </div>

      {/* 4. Users */}
      <SectionLabel>{t.sectionUsers}</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2">
        <Tile label={t.active30d} value={d.users.active_30d} />
        <Tile label={t.mfaEnabled} value={`${d.users.mfa_pct}%`} />
        <Tile label={t.onlineNow15} value={d.live.users_online} />
      </div>
      {roleItems.length > 0 && (
        <div className="mt-2">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.05em] px-4">
            {t.roleDistribution}
          </div>
          <DistroBar items={roleItems} />
        </div>
      )}

      {/* 5. AI */}
      <SectionLabel>{t.sectionAI}</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2">
        <Tile label={t.aiTriagedWk} value={d.ai.triaged_week} />
        <Tile
          label={t.aiFpRate}
          value={`${d.ai.false_positive_pct}%`}
        />
      </div>

      {/* 6. Totals — bottom, smaller weight */}
      <SectionLabel>{t.sectionTotals}</SectionLabel>
      <div className="grid grid-cols-3 gap-x-2">
        <Tile label={t.orgs} value={d.totals.orgs} />
        <Tile label={t.users} value={d.totals.users} />
        <Tile label={t.engagements} value={d.totals.engagements} />
      </div>
    </div>
  );
}
